import React, { useMemo } from 'react';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';
import { graphql } from 'react-relay';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { FormattedMessage } from 'react-intl-next';
import Button from '@atlaskit/button';
import type {
	EnableEndUserRequestLoaderQuery$data,
	EnableEndUserRequestLoaderQuery$variables,
} from './__generated__/EnableEndUserRequestLoaderQuery.graphql';
import { ConfluenceEdition } from '@confluence/session-data/entry-points/SiteInformationQuery';

// to be extended as we add in support for more touchpoints
const ELIGIBLE_TOUCHPOINTS = ['pageInsights', 'teamCalendarUpsellScreen'];

export const EnableEndUserRequestLoader = ({
	source,
}: {
	source: 'pageInsights' | 'teamCalendarUpsellScreen';
}): React.JSX.Element | null => {
	if (fg('connie_end_user_request_kill_switch')) {
		return <EnableEndUserRequestLoaderInner source={source} />;
	}

	return null;
};

// to be extended as we add in support for more touchpoints
const getOfferingName = (source: 'pageInsights' | 'teamCalendarUpsellScreen') => {
	switch (source) {
		case 'pageInsights':
			return 'Standard';
		case 'teamCalendarUpsellScreen':
			return 'Premium';
		default:
			return 'Standard';
	}
};

const EnableEndUserRequestLoaderInner = ({
	source,
}: {
	source: 'pageInsights' | 'teamCalendarUpsellScreen';
}): React.JSX.Element | null => {
	const { cloudId, userId, edition } = useSessionData();

	const offeringName = getOfferingName(source);
	const { loading, data } = useQueryInterop<
		EnableEndUserRequestLoaderQuery$data,
		EnableEndUserRequestLoaderQuery$variables
	>(
		graphql`
			query EnableEndUserRequestLoaderQuery($cloudId: ID!, $userId: ID!, $offeringName: String!) {
				tenantContexts(cloudIds: [$cloudId]) {
					entitlementInfo(hamsProductKey: "confluence.ondemand") {
						entitlementId
						entitlement {
							id
							__typename
							experienceCapabilities {
								changeOfferingV2(offeringName: $offeringName) {
									isAvailableToUser
								}
							}

							transactionAccount {
								isBillToPresent
							}

							relatesFromEntitlements {
								entitlementId
								relationshipType
							}
						}
					}
				}

				user(accountId: $userId) {
					... on AtlassianAccountUser {
						name
						locale
					}
				}

				userPreferences {
					onboardingState(key: "end-user-request-to-upgrade-latest-date") {
						key
						value
					}
				}
			}
		`,
		{
			variables: {
				cloudId,
				userId: userId!,
				offeringName,
			},
			fetchPolicy: 'cache-and-network',
			skip: !ELIGIBLE_TOUCHPOINTS.includes(source) || !userId || edition !== ConfluenceEdition.FREE,
		},
	);

	const isEligibleForEndUserRequest = useMemo(() => {
		const entitlement = data?.tenantContexts?.[0]?.entitlementInfo?.entitlement;
		const isInCollection = entitlement?.relatesFromEntitlements?.find(
			(item) => item?.relationshipType === 'COLLECTION',
		)?.entitlementId;
		const isChangeOfferingAvailable =
			entitlement?.experienceCapabilities?.changeOfferingV2?.isAvailableToUser;
		const canUpgrade = Boolean(isChangeOfferingAvailable);
		const hasPaymentDetailsEntered = Boolean(entitlement?.transactionAccount?.isBillToPresent);

		return Boolean(
			!loading &&
				!isInCollection &&
				edition === ConfluenceEdition.FREE &&
				hasPaymentDetailsEntered &&
				!canUpgrade,
		);
	}, [data, loading, edition]);

	if (!isEligibleForEndUserRequest) {
		return null;
	}

	// fire aa test exposure
	expVal('connie_end_user_request_exp_aa', 'cohort', 'not-enrolled');

	if (expVal('connie_end_user_request_exp', 'cohort', 'not-enrolled') === 'variation') {
		return (
			<Button onClick={() => {}}>
				<FormattedMessage
					id="experiment-confluence-enable-end-user-request.request-upgrade-button.label"
					defaultMessage="Request to try"
					description="Label text for the request upgrade button in the confluence enable end user request experiment"
				/>
			</Button>
		); // dummy button, replace with actual button component
	}

	return null;
};
